/**
 * pwaAppConfig — resolves the "active PWA" from the current URL and exposes
 * its registry-derived config (icon, colors, name, SW path, install gates).
 *
 * Source of truth is the codegen'd `frontend/shared/apps.generated.json`
 * (produced by `scripts/gen_frontend_apps.py`). No network calls, no
 * manual duplication — swap a value in the backend registry and it
 * propagates here on the next codegen run.
 *
 * `window.__ADVOSET_APP__` is set by the inline resolver in the root
 * shell (public/apps.resolver.js). We honour it if present; otherwise
 * we longest-prefix match `window.location.pathname` against every
 * registered `routing.scope`.
 */
import appsData from "@shared/apps.generated.json";

const APPS_BY_SLUG = (appsData?.apps || []).reduce((acc, app) => {
  acc[app.identity.id] = app;
  return acc;
}, {});

// Longest scope first so /lawyer-portal/... never mis-routes to /.
const APPS_BY_SCOPE_LEN_DESC = [...(appsData?.apps || [])].sort(
  (a, b) => b.routing.scope.length - a.routing.scope.length
);

function resolveFromPath(pathname) {
  for (const app of APPS_BY_SCOPE_LEN_DESC) {
    const scope = app.routing.scope;
    // Match "/secure-console" (no trailing slash) against scope "/secure-console/"
    // too — dashboard_route omits the slash, and mismatching here made post-login
    // API calls carry the WRONG app header (admin login appeared stuck).
    const bare = scope.length > 1 && scope.endsWith("/") ? scope.slice(0, -1) : scope;
    if (pathname === scope || pathname === bare || pathname.indexOf(scope) === 0) return app;
  }
  return null;
}

/** Return the currently-active PublicAppProjection or null.
 * URL path is the source of truth — the boot-time `__ADVOSET_APP__` hint is
 * only a fallback. (The hint is resolved once at page load, so after a
 * client-side navigation like `/` → `/lawyer-portal/login` it goes stale and
 * used to mis-scope OAuth logins to the wrong app.)
 */
export function getActiveApp() {
  if (typeof window === "undefined") return null;
  const byPath = resolveFromPath(window.location.pathname);
  if (byPath) return byPath;
  const hint = window.__ADVOSET_APP__;
  return (hint && APPS_BY_SLUG[hint]) || null;
}

/** Every registered app in the platform. */
export function listApps() {
  return appsData?.apps || [];
}

/** Look up an app by slug. */
export function getAppBySlug(slug) {
  return APPS_BY_SLUG[slug] || null;
}

/**
 * Install-time role gate.
 *
 * Returns one of:
 *   "allowed"          — go ahead, show the install prompt.
 *   "role_mismatch"    — user is signed in with a role that doesn't match.
 *   "restricted"       — app is INTERNAL and no matching session exists.
 *   "invite_only"      — app is INVITE_ONLY and no matching session exists.
 *
 * Anonymous visits to PUBLIC apps always return "allowed" so the user
 * can install first and sign in later — the platform's default flow.
 */
export function evaluateInstallGate(app, user) {
  if (!app) return "restricted";

  const visibility = app.identity?.platform_visibility;
  const appRole = app.identity?.role;
  const userRole = user?.role || null;

  // Signed-in user whose role doesn't match the active app's role.
  if (userRole && userRole !== appRole) return "role_mismatch";

  // Anonymous visitor on an internal-only app: block the install.
  if (!userRole && visibility === "internal") return "restricted";

  // Anonymous visitor on an invite-only app: soft gate — surface but
  // don't fire the browser prompt without an explicit user gesture.
  if (!userRole && visibility === "invite_only") return "invite_only";

  return "allowed";
}
